import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Menu from '../../components/menu/Menu';
import NavBar from '../../components/navbar/NavBar';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import ReactLoading from 'react-loading';
import { FaDownload, FaChartLine, FaCheckCircle, FaEdit, FaClipboardList } from 'react-icons/fa';
import {
  fetchAIPerformanceSummary,
  fetchAIPerformanceTrends,
  fetchAIPerformanceByGrouper,
  fetchAIPerformanceDistribution
} from '../../services/api/fetchAIPerformance';
import './AIPerformanceDashboard.css';

const AIPerformanceDashboard = () => {
  const { clientId } = useParams();
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [isLoading, setIsLoading] = useState(false);
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);

  // Data states
  const [summaryData, setSummaryData] = useState(null);
  const [trendsData, setTrendsData] = useState(null);
  const [grouperData, setGrouperData] = useState([]);
  const [distributionData, setDistributionData] = useState(null);

  // Error and success states
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Set default date range (last 30 days)
  useEffect(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    setDateRange([start, end]);
  }, []);

  // Fetch data when date range changes
  useEffect(() => {
    if (startDate && endDate) {
      fetchDashboardData();
    }
  }, [startDate, endDate, clientId]);

  const fetchDashboardData = async () => {
    setIsLoading(true);
    setErrorMessage('');

    try {
      const params = {
        start_date: formatDate(startDate),
        end_date: formatDate(endDate),
        client_id: clientId
      };

      // Fetch all data in parallel
      const [summary, trends, groupers, distribution] = await Promise.all([
        fetchAIPerformanceSummary(params),
        fetchAIPerformanceTrends({ ...params, frequency: 'daily' }),
        fetchAIPerformanceByGrouper(params),
        fetchAIPerformanceDistribution(params)
      ]);

      setSummaryData(summary);
      setTrendsData(trends);
      setGrouperData(groupers.results || []);
      setDistributionData(distribution);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setErrorMessage('Erro ao carregar dados do dashboard. Por favor, tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatPercentage = (value) => {
    return value ? `${value.toFixed(1)}%` : '0.0%';
  };

  // Trend Chart Configuration
  const getTrendChartOptions = () => {
    if (!trendsData || !trendsData.results || trendsData.results.length === 0) {
      return null;
    }

    const dates = trendsData.results.map(item => item.period);
    const accuracyRates = trendsData.results.map(item => item.accuracy_rate || 0);
    const revisionRates = trendsData.results.map(item => item.revision_rate || 0);

    return {
      chart: {
        type: 'line',
        backgroundColor: '#ffffff',
        height: 400
      },
      title: {
        text: 'Tendência de Performance da IA',
        style: { fontSize: '18px', fontWeight: '600' }
      },
      xAxis: {
        categories: dates,
        title: { text: 'Período' }
      },
      yAxis: {
        title: { text: 'Percentual (%)' },
        min: 0,
        max: 100
      },
      tooltip: {
        shared: true,
        valueSuffix: '%'
      },
      plotOptions: {
        line: {
          dataLabels: { enabled: false },
          enableMouseTracking: true
        }
      },
      series: [
        {
          name: 'Taxa de Accuracy',
          data: accuracyRates,
          color: '#2E7D32'
        },
        {
          name: 'Taxa de Revisão',
          data: revisionRates,
          color: '#F57C00'
        }
      ],
      credits: { enabled: false }
    };
  };

  // Distribution Chart Configuration
  const getDistributionChartOptions = () => {
    if (!distributionData) {
      return null;
    }

    const data = [
      { name: 'Aprovado', y: distributionData.approved || 0, color: '#2E7D32' },
      { name: 'Revisado', y: distributionData.revised || 0, color: '#F57C00' },
      { name: 'Auto-Aprovado', y: distributionData.auto_approved || 0, color: '#1565C0' },
      { name: 'Rejeitado', y: distributionData.rejected || 0, color: '#C62828' },
      { name: 'Pendente', y: distributionData.pending || 0, color: '#E67700' }
    ].filter(item => item.y > 0);

    return {
      chart: {
        type: 'pie',
        backgroundColor: '#ffffff',
        height: 400
      },
      title: {
        text: 'Distribuição de Status de Avaliações',
        style: { fontSize: '18px', fontWeight: '600' }
      },
      tooltip: {
        pointFormat: '<b>{point.y}</b> ({point.percentage:.1f}%)'
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: true,
            format: '<b>{point.name}</b>: {point.percentage:.1f}%'
          }
        }
      },
      series: [{
        name: 'Avaliações',
        colorByPoint: true,
        data: data
      }],
      credits: { enabled: false }
    };
  };

  const handleExport = () => {
    setSuccessMessage('Funcionalidade de exportação em desenvolvimento.');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const trendChartOptions = getTrendChartOptions();
  const distributionChartOptions = getDistributionChartOptions();

  return (
    <div className="ai-dashboard-container">
      <Menu isExpanded={isMenuExpanded} setIsExpanded={setIsMenuExpanded} />
      <div className={`ai-dashboard-content ${isMenuExpanded ? 'menu-expanded' : 'menu-collapsed'}`}>
        <NavBar />

        <div className="ai-dashboard-main">
          <div className="ai-dashboard-header">
            <h1 className="ai-dashboard-title">Dashboard de Performance da IA</h1>
            <p className="ai-dashboard-subtitle">
              Análise de métricas de avaliação e desempenho do sistema de IA
            </p>
          </div>

          {/* Filters Section */}
          <div className="ai-dashboard-filters">
            <div className="filter-group">
              <label className="filter-label">Período</label>
              <DatePicker
                selectsRange={true}
                startDate={startDate}
                endDate={endDate}
                onChange={(update) => setDateRange(update)}
                dateFormat="dd/MM/yyyy"
                className="date-picker-input"
                placeholderText="Selecione o período"
              />
            </div>
            <button onClick={handleExport} className="export-button">
              <FaDownload /> Exportar Relatório
            </button>
          </div>

          {/* Messages */}
          {errorMessage && (
            <div className="alert alert-error">
              {errorMessage}
            </div>
          )}
          {successMessage && (
            <div className="alert alert-success">
              {successMessage}
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="loading-container">
              <ReactLoading type="spin" color="#121926" height={50} width={50} />
              <p className="loading-text">Carregando dados...</p>
            </div>
          )}

          {/* Dashboard Content */}
          {!isLoading && summaryData && (
            <>
              {/* Metrics Cards */}
              <div className="metrics-grid">
                <div className="metric-card metric-accuracy">
                  <div className="metric-icon">
                    <FaCheckCircle />
                  </div>
                  <div className="metric-content">
                    <h3 className="metric-value">{formatPercentage(summaryData.accuracy_rate)}</h3>
                    <p className="metric-label">Taxa de Accuracy</p>
                    <span className="metric-detail">
                      {summaryData.total_accurate || 0} de {summaryData.total_evaluations || 0} avaliações
                    </span>
                  </div>
                </div>

                <div className="metric-card metric-revision">
                  <div className="metric-icon">
                    <FaEdit />
                  </div>
                  <div className="metric-content">
                    <h3 className="metric-value">{formatPercentage(summaryData.revision_rate)}</h3>
                    <p className="metric-label">Taxa de Revisão</p>
                    <span className="metric-detail">
                      {summaryData.total_revised || 0} revisões
                    </span>
                  </div>
                </div>

                <div className="metric-card metric-auto-approval">
                  <div className="metric-icon">
                    <FaChartLine />
                  </div>
                  <div className="metric-content">
                    <h3 className="metric-value">{formatPercentage(summaryData.auto_approval_rate)}</h3>
                    <p className="metric-label">Auto-Aprovação</p>
                    <span className="metric-detail">
                      {summaryData.total_auto_approved || 0} auto-aprovadas
                    </span>
                  </div>
                </div>

                <div className="metric-card metric-total">
                  <div className="metric-icon">
                    <FaClipboardList />
                  </div>
                  <div className="metric-content">
                    <h3 className="metric-value">{summaryData.total_evaluations || 0}</h3>
                    <p className="metric-label">Total de Avaliações</p>
                    <span className="metric-detail">
                      Período selecionado
                    </span>
                  </div>
                </div>
              </div>

              {/* Charts Section */}
              <div className="charts-section">
                {trendChartOptions && (
                  <div className="chart-container">
                    <HighchartsReact
                      highcharts={Highcharts}
                      options={trendChartOptions}
                    />
                  </div>
                )}

                {distributionChartOptions && (
                  <div className="chart-container">
                    <HighchartsReact
                      highcharts={Highcharts}
                      options={distributionChartOptions}
                    />
                  </div>
                )}
              </div>

              {/* Performance by Grouper Table */}
              {grouperData.length > 0 && (
                <div className="table-section">
                  <h2 className="table-title">Performance por Tipo de Avaliação</h2>
                  <div className="table-wrapper">
                    <table className="performance-table">
                      <thead>
                        <tr>
                          <th>Tipo de Avaliação</th>
                          <th>Total</th>
                          <th>Accuracy</th>
                          <th>Revisões</th>
                          <th>Auto-Aprovadas</th>
                          <th>Taxa de Accuracy</th>
                        </tr>
                      </thead>
                      <tbody>
                        {grouperData.map((item, index) => (
                          <tr key={index}>
                            <td className="grouper-name">{item.grouper_name || 'N/A'}</td>
                            <td>{item.total_evaluations || 0}</td>
                            <td className="accurate-count">{item.total_accurate || 0}</td>
                            <td className="revised-count">{item.total_revised || 0}</td>
                            <td className="auto-approved-count">{item.total_auto_approved || 0}</td>
                            <td className="accuracy-rate">{formatPercentage(item.accuracy_rate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Empty State */}
          {!isLoading && !summaryData && (
            <div className="empty-state">
              <FaChartLine size={64} color="#ccc" />
              <h3>Nenhum dado disponível</h3>
              <p>Selecione um período para visualizar as métricas de performance da IA.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIPerformanceDashboard;